import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { authUserUrl, verifyUserEmailUrl } from "api/endpoints";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import MDBox from "components/MDBox";
import omnioLogo from "assets/images/logos/omniofarm_colour.png";
import {
  OMNIO_SESSION_STORAGE_KEY,
  getAuthTokenCredentials,
  getStoredSession,
  hydrateStoredSession,
  persistStoredSession,
  requestAccessToken,
} from "utils/userIdentity";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [storedSession, setStoredSession] = useState(getStoredSession);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetForm, setResetForm] = useState({
    uniqueID: "",
    username: "",
    password: "",
    newPassword: "",
  });
  const [resetSaving, setResetSaving] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSuccessOpen, setResetSuccessOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const navigate = useNavigate();
  const { uniqueID: resetUniqueID } = useParams();

  const readLatestSession = useCallback(() => storedSession ?? getStoredSession(), [storedSession]);
  const hasStoredSession = Boolean(readLatestSession());

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => {};
    }

    const updateOnlineStatus = () => {
      setIsOnline(window.navigator.onLine);
    };

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    hydrateStoredSession()
      .then((session) => {
        if (isActive && session) {
          setStoredSession(session);
        }
      })
      .catch(() => {});
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => {};
    }

    const handleStorage = (event) => {
      if (event.key === OMNIO_SESSION_STORAGE_KEY) {
        setStoredSession(getStoredSession());
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (!isOnline) {
      const snapshot = readLatestSession();
      if (snapshot) {
        if (!storedSession) {
          setStoredSession(snapshot);
        }
        navigate("/facilities");
      }
    }
  }, [isOnline, navigate, readLatestSession, storedSession]);

  const preserveSession = async (session) => {
    try {
      await persistStoredSession(session);
      setStoredSession(session);
    } catch (storageError) {
      console.warn("Unable to persist login session", storageError);
    }
  };

  const handleContinueOffline = () => {
    const session = readLatestSession();
    if (session) {
      if (!storedSession) {
        setStoredSession(session);
      }
      setError("");
      navigate("/facilities");
    } else {
      setError("No saved session available for offline use.");
    }
  };

  const openResetDialog = (prefilledUniqueID = "") => {
    setResetForm((form) => ({
      ...form,
      uniqueID: prefilledUniqueID || form.uniqueID,
      username,
    }));
    setResetMessage("");
    setResetError("");
    setResetOpen(true);
  };

  useEffect(() => {
    if (resetUniqueID) {
      setResetForm((form) => ({
        ...form,
        uniqueID: resetUniqueID,
        username,
      }));
      setResetMessage("");
      setResetError("");
      setResetOpen(true);
    }
  }, [resetUniqueID, username]);

  const closeResetDialog = () => {
    if (resetSaving) return;
    setResetOpen(false);
    setResetMessage("");
    setResetError("");
  };

  const updateResetField = (field) => (event) => {
    setResetForm((form) => ({ ...form, [field]: event.target.value }));
  };

  const handleResetPassword = async () => {
    setResetSaving(true);
    setResetMessage("");
    setResetError("");

    try {
      const response = await axios.post(
        verifyUserEmailUrl(resetForm.uniqueID),
        {
          username: resetForm.username,
          password: resetForm.password,
          newPassword: resetForm.newPassword,
        },
        {
          headers: { "Content-Type": "application/json" },
          omnioOffline: { skipQueue: true, skipCache: true, skipAuthHeader: true },
          __omnioSkipAuthHeader: true,
        }
      );
      if (response.data?.success === true && response.data?.passwordSet === true) {
        setResetOpen(false);
        setResetMessage("");
        setResetError("");
        setResetSuccessOpen(true);
      } else {
        setResetMessage(response.data?.message || "Password reset submitted.");
      }
    } catch (resetRequestError) {
      console.error("Unable to reset password", resetRequestError);
      setResetError("Unable to reset your password. Please contact your administrator");
    } finally {
      setResetSaving(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!isOnline) {
      if (readLatestSession()) {
        handleContinueOffline();
      } else {
        setError("No internet connection. Connect to sign in for the first time.");
      }
      return;
    }

    try {
      const tokenCredentials = getAuthTokenCredentials(username, password);
      const tokenResponse = await requestAccessToken(tokenCredentials);
      const token = tokenResponse.accessToken;
      if (!token) {
        throw new Error("Token response did not include an accessToken");
      }

      const userInfo = {
        UniqueID: "",
        Username: username,
        Password: "",
      };

      const userResponse = await axios.post(authUserUrl(), userInfo, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Omnio-Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        omnioOffline: { skipQueue: true, skipCache: true },
      });

      const user = userResponse.data;

      if (user.registered) {
        const userLogin = {
          ID: user.id,
          Name: user.name,
          Surname: user.surname,
          token,
          email: user.email,
          contactNumber: user.contactNumber,
          userName: user.userName,
          userTypeID: user.userTypeID,
          farmId: user.farmId ?? user.farmID ?? user.FarmID,
          accessToken: token,
          expiresAt: tokenResponse.expiresAt,
          tokenExpiresAt: tokenResponse.expiresAt,
          date: new Date(),
        };

        if (!tokenCredentials.fromEnvironment) {
          userLogin.authTokenUsername = tokenCredentials.username;
          userLogin.authTokenPassword = tokenCredentials.password;
        }

        await preserveSession(userLogin);

        if (user.userTypeID === 1) {
          navigate("/facilities");
        } else {
          navigate("/facilities");
        }
      } else {
        setError("User not yet registered");
      }
    } catch (err) {
      console.error(err);
      const offlineMessage = "Unable to reach the server. Check your connection and try again.";
      setError(navigator?.onLine === false ? offlineMessage : "Invalid login credentials");
    }
  };

  return (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{ background: "transparent" }}
    >
      <Card sx={{ width: 420, padding: 4, boxShadow: 10 }}>
        <MDBox display="flex" justifyContent="center" mb={2}>
          <img src={omnioLogo} alt="Omnio Logo" style={{ width: "180px" }} />
        </MDBox>
        <CardContent>
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              sx={{
                "& label.Mui-focused": {
                  color: "#00b894",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00b894",
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              sx={{
                "& label.Mui-focused": {
                  color: "#00b894",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00b894",
                },
              }}
            />

            {!isOnline && (
              <Typography variant="caption" color="warning.main">
                Offline — you can keep working
              </Typography>
            )}

            {error && (
              <Typography variant="body2" color="error" mt={1}>
                {error}
              </Typography>
            )}

            <Stack spacing={1.5} mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                fullWidth
                sx={{ color: "grey.300" }}
              >
                Login
              </Button>
              {hasStoredSession && (
                <Button variant="text" color="secondary" fullWidth onClick={handleContinueOffline}>
                  CONTINUE OFFLINE
                </Button>
              )}
              <Button variant="text" color="secondary" fullWidth onClick={() => openResetDialog()}>
                Reset Password
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
      <Dialog open={resetOpen} onClose={closeResetDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1.5 }}>Reset Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1} alignItems="center">
            <TextField
              label="Code"
              size="small"
              value={resetForm.uniqueID}
              onChange={updateResetField("uniqueID")}
              sx={{ width: "50%" }}
            />
            <TextField
              label="Username"
              size="small"
              value={resetForm.username}
              onChange={updateResetField("username")}
              sx={{ width: "50%" }}
            />
            <TextField
              label="Current Password"
              type="password"
              size="small"
              value={resetForm.password}
              onChange={updateResetField("password")}
              sx={{ width: "50%" }}
            />
            <TextField
              label="New Password"
              type="password"
              size="small"
              value={resetForm.newPassword}
              onChange={updateResetField("newPassword")}
              sx={{ width: "50%" }}
            />
            {resetError && (
              <Typography variant="body2" color="error">
                {resetError}
              </Typography>
            )}
            {resetMessage && (
              <Typography variant="body2" color="success.main">
                {resetMessage}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" color="dark" onClick={closeResetDialog} disabled={resetSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleResetPassword}
            sx={{ color: "#fff" }}
            disabled={
              resetSaving ||
              !resetForm.uniqueID.trim() ||
              !resetForm.username.trim() ||
              !resetForm.password.trim() ||
              !resetForm.newPassword.trim()
            }
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={resetSuccessOpen}
        onClose={() => setResetSuccessOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1.5 }}>Password Reset</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text">
            Your password has successfully been reset. Please log in
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setResetSuccessOpen(false)}
            sx={{ color: "grey.300" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

export default Login;
